import React, { useState, useCallback, useMemo, useContext, useEffect } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { CloudUploadOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined, HistoryOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin, Tag } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import "../../Styles/style.css"
import { deleteUser, getHistoryUser, getUsers } from "../../Users/services/userApi";
import HistoryUser from "../component/HistoryUser";
import AddEditDriverForm from "../component/AddEditDriver";
import AddDriverMass from "../component/File/AddMassDrivers";
import { GiSteeringWheel } from "react-icons/gi";

const Drivers = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  useEffect(() => {
    document.title = `Chauffeurs | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;
  }, []);
  
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isMassModalOpen, setIsMassModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedUserHistory, setSelectedUserHistory] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetFullName, setDeleteTargetFullName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { isLoading, isError, error, data: users } = useQuery("users", getUsers);
  const drivers = users?.filter(user => user.role?.name === "DRIVER");
  console.log('drivers', drivers)

  const toggleAddEditModal = useCallback((driver = null) => {
    setIsMassModalOpen(false);
    setSelectedDriver(driver);
    setIsAddEditModalOpen(prev => !prev);
  }, []);
  
  const toggleMassModal = () => {
    setIsAddEditModalOpen(false);
    setIsMassModalOpen(prev => !prev);
  };
  

  const handleEdit = (driver) => {
    toggleAddEditModal(driver);
  };

  const handleDelete = async (driverId) => {
    setIsDeleting(true);
    try {
      await deleteUser(driverId);
      queryClient.invalidateQueries("users");
      toast.success("Le chauffeur a été supprimée avec succès");
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error(`Erreur lors de la suppression du chauffeur`);
      // toast.error(`Erreur lors de la suppression du chauffeur : ${error?.message || error}`);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmModal(false);
    }
  };
  
  const toggleHistoryModal = useCallback( async(driver = null) => {
    setSelectedDriver(driver);
    try {
      const oldVehicle = await getHistoryUser(driver?.id);
      setSelectedUserHistory(oldVehicle);
    } catch (error) {
      console.error("Error fetching user history:", error);
    }
      setIsHistoryModalOpen(prev => !prev);
    },
  );

  const handleHistory = (driver) => {
    toggleHistoryModal(driver);
  };

  const openDeleteConfirmModal = (driverId) => {
    const driverToDelete = drivers.find(driver => driver.userKeycloakId === driverId);
    if (driverToDelete) {
      setDeleteTargetId(driverId);
      setDeleteTargetFullName(driverToDelete?.name);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(() => [
    { Header: "Prénom et Nom", accessor: "name", filterable: false },
    { Header: "Nom d'utilisateur", accessor: "username", filterable: false },
    {
      Header: "Adresse email",
      accessor: "email",
      Cell: ({ row }) => (
        <div>{row.original.email || "-"}</div>
      ),
      filterable: false,
    },
    { 
      Header: "Téléphone",
      accessor: "phone",
      Cell: ({ row }) => (
      <div>{row.original.phone || "-"}</div>),
      filterable: false 
    },
    { 
      Header: "Adresse",
      accessor: "address",
      Cell: ({ row }) => (
      <div>{row.original.address || "-"}</div>),
      filterable: false 
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <HistoryOutlined title="Détail Chauffeur" size="sm" onClick={() => handleHistory(row.original)} className="btn btn-soft-primary"/>
          
          <EditOutlined
            title="Modifier"
            size="sm"
            onClick={() => handleEdit(row.original)}
            className="btn btn-soft-warning"
          />
          <DeleteOutlined
            title="Supprimer"
            size="sm"
            onClick={() => openDeleteConfirmModal(row.original.userKeycloakId)}
            className="btn btn-soft-danger"
          />
        </div>
      ),
      filterable: false,
    },
  ], [handleEdit, openDeleteConfirmModal]);

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">
        <div className="text-title mb-4"> Chauffeurs <Tag color="#5ea8adff" style={{ fontSize: '12px'}}>{drivers?.length || 0}</Tag></div>
      </div>
      <div className="d-flex gap-3">
        <Button type="button" className="active-tab" title="Ajouter plusieurs chauffeurs" onClick={toggleMassModal}>
          {" "} Ajout en masse <CloudUploadOutlined />
        </Button>
        <Button type="button" className="active-tab" title="Ajout un chauffeur" onClick={() => toggleAddEditModal(null)} >
          {" "} Ajouter un chauffeur <GiSteeringWheel/>
        </Button>
      </div>
      
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Chauffeurs" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>                  
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={drivers || []}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter ...  <Spin />
                    </div>
                  )}
                  {isError && (
                    <div className="text-center">
                      Erreur : {error?.message || error}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <AddEditDriverForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedUser={selectedDriver}
          />
          <HistoryUser
            modal={isHistoryModalOpen}
            setModal={setIsHistoryModalOpen}
            toggle={toggleHistoryModal}
            selectedUser={selectedDriver}
            userHistory={selectedUserHistory}
          />
          <AddDriverMass
            modal={isMassModalOpen}
            setModal={setIsMassModalOpen}
            toggle={toggleMassModal}
          />

          <Modal
            open={deleteConfirmModal}
            onCancel={!isDeleting ? closeDeleteConfirmModal : undefined}
            centered
            width={500}
            footer={[
              <Button
                key="cancel"
                onClick={closeDeleteConfirmModal}
                size="large"
              >
                Annuler
              </Button>,
              <Button
                key="delete"
                danger
                type="primary"
                size="large"
                onClick={() => handleDelete(deleteTargetId)}
              >
                {isDeleting
                  ? "Suppression en cours..."
                  : "Supprimer définitivement"
                }
              </Button>,
            ]}
          >
            <div className="driv-delete-modal">
              <div className="driv-delete-modal__icon-wrapper">
                <ExclamationCircleOutlined className="driv-delete-modal__icon" />
              </div>

              <h4 className="driv-delete-modal__title">
                Confirmer la suppression
              </h4>

              <p className="driv-delete-modal__description">
                Vous êtes sur le point de supprimer définitivement le chauffeur :
              </p>

              <div className="driv-delete-modal__name">
                {deleteTargetFullName}
              </div>

              <p className="driv-delete-modal__warning">
                Cette action est irréversible et les données associées ne pourront pas être récupérées.
              </p>
            </div>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Drivers;
